import {
  ActionIcon,
  Divider,
  Menu,
  MenuProps,
  useMantineTheme,
} from '@mantine/core';
import React from 'react';

import {
  AssetType,
  AssetWarrantyType,
  useRemoveWarrantyOfAsset,
} from '@portals/api/organizations';
import { useConfirmationModal } from '@portals/framework';
import { ReactComponent as More1 } from '@portals/icons/bold/more-1.svg';
import { ReactComponent as DocumentText } from '@portals/icons/linear/document-text.svg';
import { ReactComponent as Edit3 } from '@portals/icons/linear/edit-3.svg';
import { ReactComponent as Trash } from '@portals/icons/linear/trash.svg';
import { useOpenModal } from '@portals/redux';

import { useOpenDocument } from '../../../../hooks/upload-file';
import { UploadAssetWarrantyModalProps } from '../../../modals';

interface AssetWarrantyCardMenuProps {
  warranty: AssetWarrantyType;
  assetId: AssetType['id'];
}

export function AssetWarrantyCardMenu({
  warranty,
  assetId,
}: AssetWarrantyCardMenuProps) {
  const theme = useMantineTheme();
  const openModal = useOpenModal();

  const asyncConfirmation = useConfirmationModal();
  const openDocument = useOpenDocument();

  const removeWarrantyOfAsset = useRemoveWarrantyOfAsset();

  const onDeleteWarranty = async () => {
    const isConfirmed = await asyncConfirmation({
      description: `Are you sure you want the delete "${warranty.name}"?`,
    });

    if (isConfirmed) {
      removeWarrantyOfAsset.mutate({
        warrantyId: warranty.id,
        assetId,
      });
    }
  };

  const onViewDocument = () => {
    openDocument({
      url: warranty.warranty_document_url,
      onConfirm: () => window.open(warranty.warranty_document_url, '_blank'),
    });
  };

  return (
    <Menu styles={menuStyles}>
      <Menu.Target>
        <ActionIcon color="gray" data-testid="warranty-menu-button">
          <More1 color={theme.colors.gray[5]} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          icon={<Edit3 />}
          onClick={() =>
            openModal<UploadAssetWarrantyModalProps['data']>(
              'UploadAssetWarrantyModal',
              {
                assetId,
                warrantyToEdit: warranty,
              }
            )
          }
        >
          Edit
        </Menu.Item>

        <Menu.Item icon={<DocumentText />} onClick={onViewDocument}>
          View Document
        </Menu.Item>

        <Divider />

        <Menu.Item
          color="red"
          icon={<Trash />}
          onClick={onDeleteWarranty}
          data-testid="warranty-delete-item-menu"
        >
          Delete
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

const menuStyles: MenuProps['styles'] = (theme) => ({
  item: {
    paddingInline: theme.spacing.sm,
    paddingBlock: theme.spacing.xs,
  },
  itemIcon: {
    width: 16,
    height: 16,

    svg: {
      width: '100%',
      height: '100%',
    },
  },
  itemBody: {
    padding: 0,
    fontSize: theme.fontSizes.sm,
  },
});
